import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
  Theme,
} from '@mui/material';
import { unstable_useId as useId } from '@mui/utils';
import { Key } from 'react';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  PathValue,
  Validate,
} from 'react-hook-form';

export interface RhfSelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends Pick<ControllerProps<TFieldValues, TName>, 'control' | 'name'> {
  label: string;
  values: readonly PathValue<TFieldValues, TName>[];

  /**
   * Returns a human-readable name for `value` that is displayed in the select
   * dropdown. Defaults to `(value) => value.toString()`.
   */
  getValueLabel?(value: PathValue<TFieldValues, TName>): string;

  /**
   * Returns a React key for `value`. Defaults to `(value) => value.toString()`.
   */
  getValueKey?(value: PathValue<TFieldValues, TName>): Key;

  /**
   * Return an error message or `undefined` if the field has no errors.
   */
  validate?: Validate<PathValue<TFieldValues, TName>>;

  sx?: SxProps<Theme>;

  disabled?: boolean;

  onChange?: (value: PathValue<TFieldValues, TName> | undefined) => void;
}

export function RhfSelect<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  values,
  getValueLabel = toString,
  getValueKey = toString,
  validate,
  sx,
  disabled,
  onChange,
}: RhfSelectProps<TFieldValues, TName>) {
  const labelId = useId();

  return (
    <Controller
      control={control}
      name={name}
      rules={{ validate }}
      render={({ field, fieldState }) => (
        <FormControl error={!!fieldState.error} sx={sx}>
          <InputLabel id={labelId}>{label}</InputLabel>
          <Select
            disabled={disabled}
            labelId={labelId}
            label={label}
            value={field.value !== undefined ? getValueKey(field.value) : ''}
            onChange={(e) => {
              console.log(e.target.value);
              const val = values.find(
                (value) => getValueKey(value) === e.target.value,
              );
              field.onChange(val);
              onChange?.(val);
            }}
          >
            {values.map((value, index) => (
              // TODO: fix the getValueKey for unknown values provided console error.
              // <MenuItem key={getValueKey(value)} value={getValueKey(value)}>
              //   {getValueLabel(value)}
              // </MenuItem>
              <MenuItem key={index} value={getValueKey(value)}>
                {getValueLabel(value)}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{fieldState.error?.message ?? ' '}</FormHelperText>
        </FormControl>
      )}
    />
  );
}

function toString(value: unknown): string {
  return `${value}`;
}
